// @ts-nocheck
/* eslint-disable spire/avoid-dynamic-translate */
import translate from "@insite/client-framework/Translate";
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
    Typography,
} from "@insite/mobius/index";
import getColor from "@insite/mobius/utilities/getColor";
import { fixDate, sortApiReturnTable } from "@store/Framework/Common/Utilities/reportingUtils";
import Pagination from "@store/Library/Components/Reporting/ApiDataTable/DataTablePagination";
import DataTableSearchForm from "@store/Library/Components/Reporting/ApiDataTable/DataTableSearchForm";
import ButtonExportFile from "@store/Library/Components/Reporting/Common/ButtonExportFile";

import uniqueId from "lodash/uniqueId";
import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";

const DataSummary = styled.div`
    background-color: ${getColor("common.disabledLight")};
    width: 100%;
    border-radius: 0.25rem;
    padding: 0.5rem 1rem;
    margin-bottom: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    & p {
        margin-bottom: 0;
    }
`;
export interface FormatDictionary {
    [key: string]: (value: string) => string | JSX.Element | number;
}
export interface DataTableData<T> {
    title: string;
    dataSummaryTitle?: string;
    headers: string[];
    data: T[];
    itemsCount?: number;
    dataKeys: (keyof T)[] | [];
    uniqueKey?: keyof T;
    formatDictionary?: FormatDictionary;
}

export const RoundedDataTable = styled(DataTable)`
    th:first-of-type {
        border-top-left-radius: 10px;
    }
    th:last-of-type {
        border-top-right-radius: 10px;
    }
    tr:last-of-type td:first-of-type {
        border-bottom-left-radius: 10px;
    }
    tr:last-of-type td:last-of-type {
        border-bottom-right-radius: 10px;
    }
`;
interface Props {
    excelData?: DataTableData<any>[] | DataTableData<any>;
    dataTableData: DataTableData<any>;
    showExportButton?: boolean;
    searchable?: boolean;
    pagination?: boolean;
}
const ReportDataTable: React.FC<Props> = ({
    dataTableData,
    showExportButton = true,
    searchable = true,
    pagination = true,
    excelData,
}) => {
    const { dataSummaryTitle, headers, data, uniqueKey, formatDictionary } = dataTableData || ({} as DataTableData<{}>);
    const dataKeys = (dataTableData.dataKeys as string[]) || [];
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [resultsPerPage, setResultsPerPage] = useState(10);
    const [lastSort, setLastSort] = useState("");
    const [filteredData, setFilteredData] = useState(data);

    const sorted = (data: string) => {
        if (!lastSort) {
            return false;
        }
        const lastSortString = String(lastSort);
        if (lastSortString?.includes(data)) {
            if (lastSortString.includes("desc")) {
                return "descending";
            }
            return "ascending";
        }
        return "other";
    };

    useEffect(() => {
        if (data.length === 0 && searchTerm.length) {
            setSearchTerm("");
        }
        setFilteredData(data);
    }, [dataTableData]);
    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [filteredData.length, data.length]);
    const handlePageChange = (newPageNumber: number) => {
        setCurrentPage(newPageNumber);
    };

    const changeNumberOfResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newResultsPerPage = Number(event.target.value);
        const numberOfResults = searchable ? filteredData.length : data.length;
        const finalPageIndex = Math.ceil((numberOfResults - 1) / newResultsPerPage);
        if (finalPageIndex < currentPage) {
            setCurrentPage(finalPageIndex);
        }
        setResultsPerPage(newResultsPerPage);
    };

    const headerClick = (sortField: string) => {
        setCurrentPage(1);
        if (filteredData && filteredData.length > 0) {
            const newSortedData = sortApiReturnTable(filteredData, sortField, lastSort, setLastSort);
            if (newSortedData) {
                setFilteredData(newSortedData);
            }
        }
    };
    const dataToUse = searchable ? filteredData : data;
    let currentPageData: typeof data = dataToUse.slice(
        (currentPage - 1) * resultsPerPage,
        currentPage * resultsPerPage,
    );
    currentPageData = currentPageData.map(item => {
        const keys = Object.keys(item);

        const fixedDataArray = keys.map(key => {
            let object: { [key: string]: string | JSX.Element | number } = {};
            if (key.toLocaleLowerCase().includes("date") || String(item[key]).includes("T")) {
                object = { [key]: fixDate(item[key]) };
            } else {
                object = { [key]: item[key] };
            }
            if (formatDictionary && key in formatDictionary) {
                object = { [key]: formatDictionary[key](String(item[key])) };
            }
            return object;
        });

        return Object.assign({}, item, ...fixedDataArray);
    });
    return (
        <>
            {searchable && (
                <DataTableSearchForm
                    setSearchTerm={setSearchTerm}
                    searchTerm={searchTerm}
                    setFilteredData={setFilteredData}
                    dataTableData={dataTableData}
                />
            )}
            {dataSummaryTitle && (
                <DataSummary>
                    <Typography variant="p">
                        {dataSummaryTitle}: {filteredData.length}
                    </Typography>

                    {showExportButton && (
                        <ButtonExportFile disabled={!data?.length} excelData={excelData || dataTableData} />
                    )}
                </DataSummary>
            )}
            <RoundedDataTable>
                <DataTableHead
                    css={css`
                        width: 100%;
                    `}
                >
                    {headers.length > 0 &&
                        headers.map((header: string, index) => (
                            <DataTableHeader
                                key={header}
                                sorted={sorted(String(dataKeys[index]))}
                                onSortClick={() => headerClick(dataKeys[index])}
                            >
                                {translate(header)}
                            </DataTableHeader>
                        ))}
                    {data?.[0]?.["extraElement"] && (
                        <DataTableHeader
                            css={css`
                                text-align: center;
                            `}
                        >
                            {translate("Actions")}
                        </DataTableHeader>
                    )}
                </DataTableHead>

                <DataTableBody>
                    {currentPageData.length > 0 ? (
                        currentPageData.map((item, index) => {
                            return (
                                <DataTableRow key={uniqueId()}>
                                    <>
                                        {dataKeys.map(key => {
                                            if (typeof key === "string" && key in item) {
                                                return <DataTableCell key={key}>{item[key]}</DataTableCell>;
                                            }
                                            return (
                                                <DataTableCell key={String(key)}>
                                                    {translate("No data Found for Item")}
                                                </DataTableCell>
                                            );
                                        })}
                                        {currentPageData?.[index]?.["extraElement"] &&
                                            currentPageData?.[index]?.["extraElement"]}
                                    </>
                                </DataTableRow>
                            );
                        })
                    ) : (
                        <DataTableRow>
                            <DataTableCell>{translate("There were no results found.")}</DataTableCell>
                        </DataTableRow>
                    )}
                </DataTableBody>
            </RoundedDataTable>
            {data.length > 10 && pagination && (
                <Pagination
                    onChangeResultsPerPage={changeNumberOfResultsPerPage}
                    resultsCount={dataToUse?.length}
                    resultsPerPage={resultsPerPage}
                    currentPage={currentPage}
                    onChangePage={handlePageChange}
                />
            )}
        </>
    );
};

export default ReportDataTable;
